<template>
  <div class="required-items-page">
    <div class="required-items-page__header">
      <q-btn
        flat
        dense
        round
        icon="arrow_back"
        @click="$router.push('/required-supplier')"
        class="q-mr-sm"
      />
      <h2 class="required-items-page__title">{{ requiredName || 'Детали потребности' }}</h2>
      <span v-if="sbsProjectName" class="required-items-page__project">{{ sbsProjectName }}</span>
      <q-chip color="grey-3" text-color="green-8" dense icon="local_shipping" class="q-ml-sm">Поставщик</q-chip>
      <q-btn-toggle
        v-model="viewMode"
        no-caps
        dense
        unelevated
        toggle-color="primary"
        :options="[
          { value: 'week', label: 'Недели' },
          { value: 'month', label: 'Месяцы' },
          { value: 'all', label: 'Все' },
        ]"
        class="ri-view-toggle q-mx-md"
      />
      <q-btn
        flat
        dense
        no-caps
        icon="unfold_more"
        label="Развернуть"
        size="sm"
        @click="expandAll"
        class="q-mr-xs"
      />
      <q-btn
        flat
        dense
        no-caps
        icon="unfold_less"
        label="Свернуть"
        size="sm"
        @click="collapseAll"
      />
      <q-btn
        :outline="!selectionMode"
        :unelevated="selectionMode"
        no-caps
        dense
        :color="selectionMode ? 'orange' : 'grey-7'"
        icon="highlight_alt"
        :label="selectionMode ? 'Выбор активен' : 'Выбрать'"
        class="q-ml-md"
        @click="toggleSelectionMode"
      />
      <q-btn-toggle
        v-if="selectionMode"
        v-model="selectionTool"
        no-caps
        dense
        unelevated
        toggle-color="orange"
        text-color="grey-8"
        :options="[
          { value: 'rect', label: 'Область', icon: 'crop_free' },
          { value: 'click', label: 'Клик', icon: 'ads_click' },
        ]"
        class="q-ml-sm ri-tool-toggle"
      />
      <q-chip
        v-if="selectedNonEmptyCount"
        color="orange"
        text-color="white"
        dense
        class="q-ml-sm"
        removable
        @remove="clearSelection"
      >
        {{ selectedNonEmptyCount }} яч.
      </q-chip>
      <q-btn
        v-if="selectedNonEmptyCount"
        unelevated
        no-caps
        dense
        color="primary"
        icon="visibility"
        label="Показать"
        class="q-ml-sm"
        @click="showSelectionSummary"
      />
      <q-space />
    </div>

    <div v-if="progressData && progressData.smeta" class="ri-progress-section">
      <div class="ri-progress-bar">
        <div
          class="ri-progress-segment ri-progress--required"
          :style="{ width: progressBarWidth('required') }"
        ></div>
        <div
          class="ri-progress-segment ri-progress--ordered"
          :style="{ width: progressBarWidth('ordered') }"
        ></div>
        <div
          class="ri-progress-segment ri-progress--delivered"
          :style="{ width: progressBarWidth('delivered') }"
        ></div>
        <q-tooltip class="ri-progress-tooltip">
          <div class="ri-tooltip-row"><span class="ri-tooltip-dot" style="background:#ff9800"></span> Потребность: {{ fmtNum(progressData.required) }} ({{ fmtNum(progressPct('required')) }}%)</div>
          <div class="ri-tooltip-row"><span class="ri-tooltip-dot" style="background:#2196f3"></span> Заказано: {{ fmtNum(progressData.ordered) }} ({{ fmtNum(progressPct('ordered')) }}%)</div>
          <div class="ri-tooltip-row"><span class="ri-tooltip-dot" style="background:#4caf50"></span> Доставлено: {{ fmtNum(progressData.delivered) }} ({{ fmtNum(progressPct('delivered')) }}%)</div>
          <div class="ri-tooltip-row"><span class="ri-tooltip-dot" style="background:#999"></span> Смета: {{ fmtNum(progressData.smeta) }}</div>
        </q-tooltip>
      </div>
      <div class="ri-progress-legend">
        <div class="ri-legend-item" v-if="progressData.required > 0">
          <span class="ri-legend-dot" style="background:#ff9800"></span>
          <span class="ri-legend-text">Потребность: {{ fmtNum(progressData.required) }} ({{ fmtNum(progressPct('required')) }}%)</span>
        </div>
        <div class="ri-legend-item" v-if="progressData.ordered > 0">
          <span class="ri-legend-dot" style="background:#2196f3"></span>
          <span class="ri-legend-text">Заказано: {{ fmtNum(progressData.ordered) }} ({{ fmtNum(progressPct('ordered')) }}%)</span>
        </div>
        <div class="ri-legend-item" v-if="progressData.delivered > 0">
          <span class="ri-legend-dot" style="background:#4caf50"></span>
          <span class="ri-legend-text">Доставлено: {{ fmtNum(progressData.delivered) }} ({{ fmtNum(progressPct('delivered')) }}%)</span>
        </div>
        <div class="ri-legend-item">
          <span class="ri-legend-dot" style="background:#ccc"></span>
          <span class="ri-legend-text">Смета: {{ fmtNum(progressData.smeta) }}</span>
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="column items-center q-pa-lg">
      <q-spinner color="primary" size="40px" />
      <div class="q-mt-sm">Загрузка данных...</div>
    </div>

    <div
      v-if="!isLoading"
      ref="tableWrapper"
      class="required-items-table-wrapper"
      :class="{
        'ri-selecting-mode': selectionMode,
        'ri-tool-rect': selectionMode && selectionTool === 'rect',
        'ri-tool-click': selectionMode && selectionTool === 'click',
        'ri-dragging': isSelecting,
      }"
      @mousedown="onSelectionMouseDown"
    >
      <div
        v-if="isSelecting"
        class="ri-selection-rect"
        :style="selectionRectStyle"
      ></div>
      <table class="ri-table">
        <thead ref="thead">
          <tr ref="headerRow1">
            <th rowspan="3" class="ri-th ri-th--fixed ri-sticky ri-sticky--0" title="#">#</th>
            <th rowspan="3" class="ri-th ri-th--fixed ri-th--name ri-sticky ri-sticky--1" title="Наименование"><q-icon name="list_alt" size="xs" /></th>
            <th rowspan="3" class="ri-th ri-th--fixed ri-sticky ri-sticky--2" title="Смета"><q-icon name="description" size="xs" /></th>
            <th rowspan="3" class="ri-th ri-th--fixed ri-sticky ri-sticky--3" title="Общее кол-во"><q-icon name="functions" size="xs" /></th>
            <th rowspan="3" class="ri-th ri-th--fixed ri-sticky ri-sticky--4" title="Приход"><q-icon name="local_shipping" size="xs" /></th>
            <th rowspan="3" class="ri-th ri-th--fixed ri-sticky ri-sticky--5" title="Остаток"><q-icon name="inventory" size="xs" /></th>
            <th
              v-for="group in displayGroups"
              :key="group.key"
              :colspan="group.colCount * 2"
              class="ri-th ri-th--month"
              :class="'ri-month--' + group.monthIdx"
            >
              <span class="ri-month-label">{{ group.label }}</span>
            </th>
          </tr>
          <tr ref="headerRow2">
            <th
              v-for="(col, colIdx) in displayColumns"
              :key="col.key"
              colspan="2"
              class="ri-th ri-th--day"
              :class="[
                'ri-month--' + col.monthIdx,
                { 'ri-th--click-target': selectionMode && selectionTool === 'click' }
              ]"
              :style="{ top: headerRow1Height + 'px' }"
              @click="onColumnHeaderClick(colIdx)"
            >
              {{ col.label }}
            </th>
          </tr>
          <tr>
            <template v-for="col in displayColumns" :key="'s-' + col.key">
              <th class="ri-th ri-th--sub" :class="'ri-month--' + col.monthIdx" :style="{ top: (headerRow1Height + headerRow2Height) + 'px' }">Кол-во</th>
              <th class="ri-th ri-th--sub" :class="'ri-month--' + col.monthIdx" :style="{ top: (headerRow1Height + headerRow2Height) + 'px' }">Приход</th>
            </template>
          </tr>
        </thead>
        <tbody ref="levelBody">
          <tr
            v-for="lvRow in levelRows"
            :key="'lvrow-' + lvRow.rowIndex"
            class="ri-row ri-row--levels"
            :style="{ '--header-h': theadHeight + 'px' }"
          >
            <td class="ri-td ri-td--level-sticky ri-sticky ri-sticky--0"></td>
            <td class="ri-td ri-td--level-sticky ri-td--level-label ri-sticky ri-sticky--1">
              <q-icon name="layers" size="xs" class="q-mr-xs" />
              Этажи
            </td>
            <td class="ri-td ri-td--level-sticky ri-sticky ri-sticky--2"></td>
            <td class="ri-td ri-td--level-sticky ri-sticky ri-sticky--3"></td>
            <td class="ri-td ri-td--level-sticky ri-sticky ri-sticky--4"></td>
            <td class="ri-td ri-td--level-sticky ri-sticky ri-sticky--5"></td>
            <template v-for="(seg, si) in lvRow.segments" :key="'lvseg-' + si">
              <td
                v-if="seg.type === 'level'"
                :colspan="seg.span * 2"
                class="ri-td ri-td--level-sticky ri-td--level-active"
                :class="'ri-month--' + seg.monthIdx"
              >
                <span class="ri-level-label">{{ seg.level_num }} этаж</span>
              </td>
              <td
                v-else
                colspan="2"
                class="ri-td ri-td--level-sticky"
                :class="'ri-month--' + seg.monthIdx"
              ></td>
            </template>
          </tr>
          <tr
            v-if="isLevel && !levelRows.length"
            class="ri-row ri-row--levels"
            :style="{ '--header-h': theadHeight + 'px' }"
          >
            <td class="ri-td ri-td--level-sticky ri-sticky ri-sticky--0"></td>
            <td class="ri-td ri-td--level-sticky ri-td--level-label ri-sticky ri-sticky--1">
              <q-icon name="layers" size="xs" class="q-mr-xs" />
              Этажи
            </td>
            <td class="ri-td ri-td--level-sticky ri-sticky ri-sticky--2"></td>
            <td class="ri-td ri-td--level-sticky ri-sticky ri-sticky--3"></td>
            <td class="ri-td ri-td--level-sticky ri-sticky ri-sticky--4"></td>
            <td class="ri-td ri-td--level-sticky ri-sticky ri-sticky--5"></td>
            <template v-for="col in displayColumns" :key="'lvl-' + col.key">
              <td
                colspan="2"
                class="ri-td ri-td--level-sticky"
                :class="'ri-month--' + col.monthIdx"
              ></td>
            </template>
          </tr>
        </tbody>
        <tbody v-for="(mat, idx) in materials" :key="mat.material_id">
            <tr
              class="ri-row ri-row--material"
              :class="{ 'ri-row--material-sticky': expanded[mat.material_id] }"
              :style="expanded[mat.material_id] ? { '--thead-h': (theadHeight + levelBodyHeight) + 'px' } : {}"
              @click="onMaterialRowClick(mat.material_id, idx)"
            >
              <td class="ri-td ri-sticky ri-sticky--0">{{ idx + 1 }}</td>
              <td
                class="ri-td ri-td--name ri-sticky ri-sticky--1"
                :class="{ 'ri-td--click-target': selectionMode && selectionTool === 'click' }"
              >
                <div class="ri-name-cell">
                  <q-icon
                    :name="expanded[mat.material_id] ? 'expand_less' : 'expand_more'"
                    size="sm"
                    class="expand-icon"
                  />
                  <span class="ri-name-text">{{ mat.material_name }}</span>
                </div>
              </td>
              <td class="ri-td ri-td--num ri-sticky ri-sticky--2">{{ mat.smeta_amount }} {{ mat.unit_name }}</td>
              <td class="ri-td ri-td--num ri-sticky ri-sticky--3" :class="{ 'ri-td--complete': mat.total_amount && mat.total_amount === mat.total_delivered }">{{ mat.total_amount || 0 }}</td>
              <td class="ri-td ri-td--num ri-sticky ri-sticky--4" :class="{ 'ri-td--complete': mat.total_amount && mat.total_amount === mat.total_delivered }">{{ mat.total_delivered }}</td>
              <td class="ri-td ri-td--num ri-sticky ri-sticky--5" :class="{ 'ri-td--complete': mat.total_amount && mat.total_amount === mat.total_delivered }">{{ mat.remaining }}</td>
              <template v-for="(col, colIdx) in displayColumns" :key="col.key">
                <td
                  class="ri-td ri-td--num ri-td--date"
                  :class="[
                    'ri-month--' + col.monthIdx,
                    { 'ri-td--selected': selectedCells[`mat-${idx}-col-${colIdx}`] },
                    { 'ri-td--click-target': selectionMode && selectionTool === 'click' }
                  ]"
                  :data-selectable="selectionMode || undefined"
                  :data-mat-idx="idx"
                  :data-col-idx="colIdx"
                  data-row-type="material"
                  @click.stop="onCellClick('material', idx, null, colIdx)"
                >
                  {{ getColAggregatedVal(mat._dateMap, col, 'amount') }}
                </td>
                <td
                  class="ri-td ri-td--num ri-td--date"
                  :class="'ri-month--' + col.monthIdx"
                  @click.stop="onCellClick('material', idx, null, colIdx)"
                >
                  {{ getColAggregatedVal(mat._dateMap, col, 'delivered') }}
                </td>
              </template>
            </tr>

            <template v-if="expanded[mat.material_id]">
              <tr
                v-for="(entry, entryIdx) in mat.entries"
                :key="`${mat.material_id}-${entry.entrance}-${entry.level_num}`"
                class="ri-row ri-row--entry"
              >
                <td class="ri-td ri-sticky ri-sticky--0"></td>
                <td class="ri-td ri-td--entry-name ri-sticky ri-sticky--1">{{ entry.label }}</td>
                <td class="ri-td ri-td--num ri-sticky ri-sticky--2">{{ entry.smeta_amount }} {{ entry.unit_name }}</td>
                <td class="ri-td ri-td--num ri-sticky ri-sticky--3" :class="{ 'ri-td--complete': getEntryTotalAmount(entry) && getEntryTotalAmount(entry) === entry.total_delivered }">{{ getEntryTotalAmount(entry) || '' }}</td>
                <td class="ri-td ri-td--num ri-sticky ri-sticky--4" :class="{ 'ri-td--complete': getEntryTotalAmount(entry) && getEntryTotalAmount(entry) === entry.total_delivered }">{{ entry.total_delivered }}</td>
                <td class="ri-td ri-td--num ri-sticky ri-sticky--5" :class="{ 'ri-td--complete': getEntryTotalAmount(entry) && getEntryTotalAmount(entry) === entry.total_delivered }">{{ entry.remaining }}</td>
                <template v-for="(col, colIdx) in displayColumns" :key="col.key">
                  <td
                    class="ri-td ri-td--num ri-td--readonly"
                    :class="[
                      'ri-month--' + col.monthIdx,
                      { 'ri-td--selected': selectedCells[`entry-${idx}-${entryIdx}-col-${colIdx}`] },
                      { 'ri-td--click-target': selectionMode && selectionTool === 'click' }
                    ]"
                    :data-selectable="selectionMode || undefined"
                    :data-mat-idx="idx"
                    :data-entry-idx="entryIdx"
                    :data-col-idx="colIdx"
                    data-row-type="entry"
                    @click.stop="onCellClick('entry', idx, entryIdx, colIdx)"
                  >
                    {{ getColAggregatedVal(entry._dateItems, col, 'amount') }}
                  </td>
                  <td
                    class="ri-td ri-td--num ri-td--readonly ri-td--delivered"
                    :class="'ri-month--' + col.monthIdx"
                    @click.stop="onCellClick('entry', idx, entryIdx, colIdx)"
                  >
                    {{ getColAggregatedVal(entry._dateItems, col, 'delivered') }}
                  </td>
                </template>
              </tr>
            </template>
        </tbody>
      </table>
    </div>

    <q-dialog v-model="showSelectionDialog">
      <q-card style="min-width: 600px; max-width: 90vw;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Выбранные материалы</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>
        <q-card-section>
          <q-markup-table flat bordered dense separator="cell" class="ri-summary-table">
            <thead>
              <tr>
                <th class="text-left">Материал</th>
                <th class="text-left">Ед.</th>
                <th class="text-right">Кол-во</th>
                <th class="text-right">Доставлено</th>
                <th class="text-right">Остаток</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, i) in selectionSummary" :key="i">
                <td class="text-left">{{ item.material_name }}</td>
                <td class="text-left">{{ item.unit_name }}</td>
                <td class="text-right text-weight-bold">{{ item.total_amount }}</td>
                <td class="text-right">{{ item.total_delivered }}</td>
                <td class="text-right" :class="{ 'text-negative': item.remaining > 0 }">{{ item.remaining }}</td>
              </tr>
              <tr v-if="!selectionSummary.length">
                <td colspan="5" class="text-center text-grey">Нет данных</td>
              </tr>
            </tbody>
          </q-markup-table>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Закрыть" color="grey" v-close-popup no-caps />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import instance from "@/services/api";

export default {
  name: "RequiredItemsSupplier",
  props: {
    id: [String, Number],
  },
  data() {
    return {
      materials: [],
      calendarDates: [],
      isLoading: false,
      progressData: null,
      expanded: {},
      viewMode: 'week',
      requiredName: '',
      sbsProjectName: '',
      dateBegin: null,
      dateEnd: null,
      isLevel: false,
      levelRanges: [],
      theadHeight: 0,
      levelBodyHeight: 0,
      headerRow1Height: 0,
      headerRow2Height: 0,
      selectionMode: false,
      selectionTool: 'rect',
      isSelecting: false,
      selectionStart: null,
      selectionCurrent: null,
      selectedCells: {},
      selectionSummary: [],
      showSelectionDialog: false,
    };
  },
  computed: {
    selectionRectStyle() {
      if (!this.selectionStart || !this.selectionCurrent) return {};
      const x = Math.min(this.selectionStart.x, this.selectionCurrent.x);
      const y = Math.min(this.selectionStart.y, this.selectionCurrent.y);
      const w = Math.abs(this.selectionCurrent.x - this.selectionStart.x);
      const h = Math.abs(this.selectionCurrent.y - this.selectionStart.y);
      return { left: x + 'px', top: y + 'px', width: w + 'px', height: h + 'px' };
    },

    selectedNonEmptyCount() {
      let count = 0;
      for (const info of Object.values(this.selectedCells)) {
        const mat = this.materials[info.matIdx];
        if (!mat) continue;
        const col = this.displayColumns[info.colIdx];
        if (!col) continue;

        let sum = 0;
        if (info.rowType === 'entry') {
          const entry = mat.entries[info.entryIdx];
          if (!entry) continue;
          for (const d of col.dates) {
            const di = entry._dateItems[d];
            if (di) sum += di.amount || 0;
          }
        } else {
          for (const d of col.dates) {
            const di = mat._dateMap[d];
            if (di) sum += di.amount || 0;
          }
        }
        if (sum) count++;
      }
      return count;
    },

    displayColumns() {
      let cols;

      if (this.viewMode === 'week') {
        cols = [];
        let cur = null;
        for (const d of this.calendarDates) {
          const wk = this.getWeekKey(d);
          if (!cur || cur.key !== wk) {
            cur = { key: wk, dates: [] };
            cols.push(cur);
          }
          cur.dates.push(d);
        }
        for (const w of cols) {
          w.label = `${this.formatShortDate(w.dates[0])} - ${this.formatShortDate(w.dates[w.dates.length - 1])}`;
        }
      } else if (this.viewMode === 'month') {
        cols = [];
        let cur = null;
        for (const d of this.calendarDates) {
          const p = d.split('-');
          const mk = `${p[0]}-${p[1]}`;
          if (!cur || cur.key !== mk) {
            const dt = new Date(parseInt(p[0]), parseInt(p[1]) - 1, 1);
            cur = {
              key: mk,
              label: dt.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' }),
              dates: [],
            };
            cols.push(cur);
          }
          cur.dates.push(d);
        }
      } else {
        cols = [{ key: 'all', label: 'Итого', dates: [...this.calendarDates] }];
      }

      let idx = 0;
      let prevMk = null;
      for (const col of cols) {
        const p = col.dates[0].split('-');
        const mk = `${p[0]}-${p[1]}`;
        if (prevMk !== null && mk !== prevMk) idx = 1 - idx;
        prevMk = mk;
        col.monthIdx = idx;
      }
      return cols;
    },

    displayGroups() {
      if (this.viewMode === 'month' || this.viewMode === 'all') {
        return this.displayColumns.map(col => ({
          key: col.key,
          label: col.label,
          colCount: 1,
          monthIdx: col.monthIdx,
        }));
      }

      const groups = [];
      let currentKey = null;
      let currentGroup = null;
      for (const col of this.displayColumns) {
        const p = col.dates[0].split('-');
        const mk = `${p[0]}-${p[1]}`;
        if (mk !== currentKey) {
          currentKey = mk;
          const dt = new Date(parseInt(p[0]), parseInt(p[1]) - 1, 1);
          currentGroup = {
            key: mk,
            label: dt.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' }),
            colCount: 0,
            monthIdx: col.monthIdx,
          };
          groups.push(currentGroup);
        }
        currentGroup.colCount++;
      }
      return groups;
    },

    levelRows() {
      if (!this.levelRanges.length || !this.displayColumns.length) return [];
      const cols = this.displayColumns;
      const sorted = [...this.levelRanges].sort((a, b) => a.start_date.localeCompare(b.start_date));

      const segments = [];
      let i = 0;
      for (const lv of sorted) {
        while (i < cols.length && cols[i].dates[0] < lv.start_date) {
          segments.push({ type: 'empty', span: 1, monthIdx: cols[i].monthIdx, col: cols[i] });
          i++;
        }
        const startIdx = i;
        while (i < cols.length && cols[i].dates[0] <= lv.end_date) {
          i++;
        }
        if (i > startIdx) {
          segments.push({
            type: 'level',
            required_level_id: lv.required_level_id,
            level_num: lv.level_num,
            span: i - startIdx,
            monthIdx: cols[startIdx].monthIdx,
            col: cols[startIdx],
          });
        }
      }
      while (i < cols.length) {
        segments.push({ type: 'empty', span: 1, monthIdx: cols[i].monthIdx, col: cols[i] });
        i++;
      }
      return [{ rowIndex: 0, segments }];
    },

  },
  mounted() {
    this.loadData();
    this._onMouseMove = this.onSelectionMouseMove.bind(this);
    this._onMouseUp = this.onSelectionMouseUp.bind(this);
  },
  beforeUnmount() {
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);
  },
  methods: {
    fmtNum(v) {
      if (v == null || v === '') return '';
      const n = Number(v);
      if (isNaN(n)) return v;
      return parseFloat(n.toFixed(2));
    },
    progressPct(type) {
      if (!this.progressData || !this.progressData.smeta) return 0;
      const val = this.progressData[type] || 0;
      return Math.min(parseFloat((val / this.progressData.smeta * 100).toFixed(1)), 100);
    },
    progressBarWidth(type) {
      const pct = this.progressPct(type);
      if (pct <= 0) return '0';
      const minPx = { required: 24, ordered: 16, delivered: 8 };
      return `max(${pct}%, ${minPx[type] || 8}px)`;
    },
    async loadData() {
      this.isLoading = true;
      try {
        const [itemsResp, progressResp] = await Promise.all([
          instance().get(`/required/${this.id}/items/`),
          instance().get(`/required/${this.id}/progress/`).catch(() => null),
        ]);
        const data = itemsResp.data;
        const raw = data?.data || [];
        this.requiredName = data?.required_name || '';
        this.sbsProjectName = data?.sbs_project_name || '';
        this.dateBegin = data?.date_begin ? this.parseDateDMY(String(data.date_begin)) : null;
        this.dateEnd = data?.date_end ? this.parseDateDMY(String(data.date_end)) : null;
        this.isLevel = !!data?.is_level;
        this.processData(raw);
        if (this.isLevel) {
          this.buildLevelRanges(data?.level_dates || []);
        }
        const pRows = progressResp?.data?.data;
        if (pRows && pRows.length) {
          const p = pRows[0];
          const toNum = (v) => parseFloat(String(v).replace(',', '.')) || 0;
          this.progressData = {
            smeta: toNum(p.smeta_amount),
            required: toNum(p.required_amount),
            ordered: toNum(p.ordered_amount),
            delivered: toNum(p.delivered_amount),
          };
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
        this.$nextTick(() => this.measureThead());
      }
    },

    measureThead() {
      if (this.$refs.headerRow1) {
        this.headerRow1Height = this.$refs.headerRow1.offsetHeight;
      }
      if (this.$refs.headerRow2) {
        this.headerRow2Height = this.$refs.headerRow2.offsetHeight;
      }
      if (this.$refs.thead) {
        this.theadHeight = this.$refs.thead.offsetHeight;
      }
      if (this.$refs.levelBody) {
        this.levelBodyHeight = this.$refs.levelBody.offsetHeight;
      } else {
        this.levelBodyHeight = 0;
      }
    },

    processData(raw) {
      const allDates = new Set();

      for (const mat of raw) {
        mat.entries = [];
        mat._dateMap = {};

        for (const block of mat.blocks || []) {
          const entry = {
            entrance: block.entrance,
            level_num: block.level_num,
            level_name: block.level_name,
            required_material_id: block.required_material_id || mat.required_material_id,
            label: `Блок ${block.entrance}`,
            smeta_amount: block.smeta_amount || 0,
            unit_name: mat.unit_name || '',
            total_delivered: 0,
            remaining: 0,
            _dateItems: {},
          };

          for (const ri of block.required_items || []) {
            const d = ri.shipping_date;
            if (!d) continue;
            allDates.add(d);

            if (!entry._dateItems[d]) {
              entry._dateItems[d] = {
                amount: 0,
                delivered: 0,
                required_item_id: null,
              };
            }
            entry._dateItems[d].amount += ri.amount || 0;
            entry._dateItems[d].delivered += ri.delivered || 0;
            if (ri.required_item_id) {
              entry._dateItems[d].required_item_id = ri.required_item_id;
            }
          }

          let entryTotalAmount = 0;
          for (const di of Object.values(entry._dateItems)) {
            entry.total_delivered = parseFloat((entry.total_delivered + di.delivered).toFixed(10));
            entryTotalAmount = parseFloat((entryTotalAmount + (di.amount || 0)).toFixed(10));
          }
          entry.remaining = parseFloat((entryTotalAmount - entry.total_delivered).toFixed(10));

          mat.entries.push(entry);
        }

        this.recalcMaterialDateMap(mat);
      }

      this.calendarDates = this.generateCalendarDates(allDates);
      this.materials = raw;
    },

    generateCalendarDates(existingDates) {
      let minDate, maxDate;

      if (this.dateBegin && this.dateEnd) {
        minDate = new Date(this.dateBegin);
        maxDate = new Date(this.dateEnd);
      } else {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        minDate = new Date(today);
        maxDate = new Date(today);
        maxDate.setDate(maxDate.getDate() + 60);
        for (const d of existingDates) {
          const dt = new Date(d);
          if (dt < minDate) minDate = new Date(dt);
          if (dt > maxDate) maxDate = new Date(dt);
        }
      }

      const minDay = minDate.getDay();
      minDate.setDate(minDate.getDate() - (minDay === 0 ? 6 : minDay - 1));

      const maxDay = maxDate.getDay();
      if (maxDay !== 0) maxDate.setDate(maxDate.getDate() + (7 - maxDay));

      const dates = [];
      const current = new Date(minDate);
      while (current <= maxDate) {
        dates.push(this.toDateString(current));
        current.setDate(current.getDate() + 1);
      }
      return dates;
    },

    toDateString(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },

    recalcMaterialDateMap(mat) {
      mat._dateMap = {};
      mat.total_delivered = 0;
      mat.smeta_amount = 0;
      let totalAmount = 0;
      for (const entry of mat.entries) {
        mat.smeta_amount = parseFloat((mat.smeta_amount + entry.smeta_amount).toFixed(10));
        mat.total_delivered = parseFloat((mat.total_delivered + entry.total_delivered).toFixed(10));
        for (const [d, vals] of Object.entries(entry._dateItems)) {
          if (!mat._dateMap[d])
            mat._dateMap[d] = { amount: 0, delivered: 0 };
          mat._dateMap[d].amount += vals.amount || 0;
          mat._dateMap[d].delivered += vals.delivered || 0;
          totalAmount += vals.amount || 0;
        }
      }
      mat.total_amount = parseFloat(totalAmount.toFixed(10));
      mat.remaining = parseFloat((totalAmount - mat.total_delivered).toFixed(10));
    },

    toggleExpand(materialId) {
      this.expanded = {
        ...this.expanded,
        [materialId]: !this.expanded[materialId],
      };
    },

    expandAll() {
      const exp = {};
      for (const mat of this.materials) {
        exp[mat.material_id] = true;
      }
      this.expanded = exp;
    },

    collapseAll() {
      this.expanded = {};
    },

    getWeekKey(dateStr) {
      const d = new Date(dateStr);
      const day = d.getDay();
      const diff = (day === 0 ? -6 : 1) - day;
      const monday = new Date(d);
      monday.setDate(d.getDate() + diff);
      return this.toDateString(monday);
    },

    formatShortDate(dateStr) {
      const parts = dateStr.split('-');
      return `${parseInt(parts[2])}.${parts[1]}`;
    },

    getColAggregatedVal(dateMap, col, field) {
      let sum = 0;
      for (const d of col.dates) {
        const item = dateMap && dateMap[d];
        if (item) sum += item[field] || 0;
      }
      return sum || '';
    },

    getEntryTotalAmount(entry) {
      let sum = 0;
      for (const di of Object.values(entry._dateItems)) {
        sum += di.amount || 0;
      }
      return sum;
    },

    parseDateDMY(ddmmyyyy) {
      if (!ddmmyyyy) return null;
      if (ddmmyyyy.includes('-')) return ddmmyyyy;
      const p = ddmmyyyy.split('.');
      if (p.length !== 3) return ddmmyyyy;
      return `${p[2]}-${p[1]}-${p[0]}`;
    },

    buildLevelRanges(levelDates) {
      this.levelRanges = (levelDates || []).map(ld => ({
        required_level_id: ld.required_level_id,
        level_num: ld.level_num,
        start_date: this.parseDateDMY(ld.start_date),
        end_date: this.parseDateDMY(ld.end_date),
      })).filter(ld => ld.start_date && ld.end_date);
    },

    toggleSelectionMode() {
      this.selectionMode = !this.selectionMode;
      if (!this.selectionMode) {
        this.clearSelection();
      }
    },

    clearSelection() {
      this.selectedCells = {};
      this.isSelecting = false;
      this.selectionStart = null;
      this.selectionCurrent = null;
    },

    getCellAmount(rowType, matIdx, entryIdx, colIdx) {
      const mat = this.materials[matIdx];
      if (!mat) return 0;
      const col = this.displayColumns[colIdx];
      if (!col) return 0;
      let sum = 0;
      if (rowType === 'entry') {
        const entry = mat.entries[entryIdx];
        if (!entry) return 0;
        for (const d of col.dates) {
          const di = entry._dateItems[d];
          if (di) sum += di.amount || 0;
        }
      } else {
        for (const d of col.dates) {
          const di = mat._dateMap[d];
          if (di) sum += di.amount || 0;
        }
      }
      return sum;
    },

    toggleCellKey(key, info) {
      const copy = { ...this.selectedCells };
      if (copy[key]) {
        delete copy[key];
      } else {
        copy[key] = info;
      }
      this.selectedCells = copy;
    },

    onMaterialRowClick(materialId, matIdx) {
      if (this.selectionMode && this.selectionTool === 'click') {
        this.onMaterialCtrlClick(matIdx);
        return;
      }
      this.toggleExpand(materialId);
    },

    onMaterialCtrlClick(matIdx) {
      const copy = { ...this.selectedCells };
      const mat = this.materials[matIdx];
      if (!mat) return;

      const candidates = [];
      for (let colIdx = 0; colIdx < this.displayColumns.length; colIdx++) {
        if (this.getCellAmount('material', matIdx, null, colIdx))
          candidates.push({ key: `mat-${matIdx}-col-${colIdx}`, info: { matIdx, colIdx, rowType: 'material' } });
        for (let entryIdx = 0; entryIdx < mat.entries.length; entryIdx++) {
          if (this.getCellAmount('entry', matIdx, entryIdx, colIdx))
            candidates.push({ key: `entry-${matIdx}-${entryIdx}-col-${colIdx}`, info: { matIdx, entryIdx, colIdx, rowType: 'entry' } });
        }
      }
      if (!candidates.length) return;

      const allSelected = candidates.every(c => copy[c.key]);
      if (allSelected) {
        for (const c of candidates) delete copy[c.key];
      } else {
        for (const c of candidates) copy[c.key] = c.info;
      }
      this.selectedCells = copy;
    },

    onColumnHeaderClick(colIdx) {
      if (!this.selectionMode || this.selectionTool !== 'click') return;

      const copy = { ...this.selectedCells };
      const candidates = [];
      for (let matIdx = 0; matIdx < this.materials.length; matIdx++) {
        const mat = this.materials[matIdx];
        if (this.getCellAmount('material', matIdx, null, colIdx))
          candidates.push({ key: `mat-${matIdx}-col-${colIdx}`, info: { matIdx, colIdx, rowType: 'material' } });
        for (let entryIdx = 0; entryIdx < mat.entries.length; entryIdx++) {
          if (this.getCellAmount('entry', matIdx, entryIdx, colIdx))
            candidates.push({ key: `entry-${matIdx}-${entryIdx}-col-${colIdx}`, info: { matIdx, entryIdx, colIdx, rowType: 'entry' } });
        }
      }
      if (!candidates.length) return;

      const allSelected = candidates.every(c => copy[c.key]);
      if (allSelected) {
        for (const c of candidates) delete copy[c.key];
      } else {
        for (const c of candidates) copy[c.key] = c.info;
      }
      this.selectedCells = copy;
    },

    onCellClick(rowType, matIdx, entryIdx, colIdx) {
      if (!this.selectionMode || this.selectionTool !== 'click') return;
      if (!this.getCellAmount(rowType, matIdx, entryIdx, colIdx)) return;

      if (rowType === 'entry') {
        const key = `entry-${matIdx}-${entryIdx}-col-${colIdx}`;
        this.toggleCellKey(key, { matIdx, entryIdx, colIdx, rowType });
      } else {
        const key = `mat-${matIdx}-col-${colIdx}`;
        this.toggleCellKey(key, { matIdx, colIdx, rowType });
      }
    },

    onSelectionMouseDown(e) {
      if (!this.selectionMode) return;
      if (this.selectionTool !== 'rect') return;
      if (e.button !== 0) return;

      const wrapper = this.$refs.tableWrapper;
      if (!wrapper) return;

      const target = e.target.closest('td');
      if (target && target.closest('.ri-sticky')) return;

      e.preventDefault();
      const rect = wrapper.getBoundingClientRect();
      this.selectionStart = {
        x: e.clientX - rect.left + wrapper.scrollLeft,
        y: e.clientY - rect.top + wrapper.scrollTop,
      };
      this.selectionCurrent = { ...this.selectionStart };
      this.isSelecting = true;

      document.addEventListener('mousemove', this._onMouseMove);
      document.addEventListener('mouseup', this._onMouseUp);
    },

    onSelectionMouseMove(e) {
      if (!this.isSelecting) return;
      const wrapper = this.$refs.tableWrapper;
      if (!wrapper) return;

      const rect = wrapper.getBoundingClientRect();
      this.selectionCurrent = {
        x: Math.max(0, e.clientX - rect.left + wrapper.scrollLeft),
        y: Math.max(0, e.clientY - rect.top + wrapper.scrollTop),
      };

      this.detectIntersectingCells();
    },

    onSelectionMouseUp(e) {
      document.removeEventListener('mousemove', this._onMouseMove);
      document.removeEventListener('mouseup', this._onMouseUp);

      if (!this.isSelecting) return;
      this.isSelecting = false;

      const dx = Math.abs((this.selectionCurrent?.x || 0) - (this.selectionStart?.x || 0));
      const dy = Math.abs((this.selectionCurrent?.y || 0) - (this.selectionStart?.y || 0));
      if (dx < 5 && dy < 5) {
        this.selectionStart = null;
        this.selectionCurrent = null;
        return;
      }

      this.detectIntersectingCells();
      this.selectionStart = null;
      this.selectionCurrent = null;

      if (Object.keys(this.selectedCells).length) {
        this.showSelectionSummary();
      }
    },

    detectIntersectingCells() {
      const wrapper = this.$refs.tableWrapper;
      if (!wrapper || !this.selectionStart || !this.selectionCurrent) return;

      const wrapperRect = wrapper.getBoundingClientRect();

      const sx = Math.min(this.selectionStart.x, this.selectionCurrent.x);
      const sy = Math.min(this.selectionStart.y, this.selectionCurrent.y);
      const ex = Math.max(this.selectionStart.x, this.selectionCurrent.x);
      const ey = Math.max(this.selectionStart.y, this.selectionCurrent.y);

      const selViewport = {
        left: sx - wrapper.scrollLeft + wrapperRect.left,
        top: sy - wrapper.scrollTop + wrapperRect.top,
        right: ex - wrapper.scrollLeft + wrapperRect.left,
        bottom: ey - wrapper.scrollTop + wrapperRect.top,
      };

      const cells = wrapper.querySelectorAll('[data-selectable]');
      const newSelected = {};

      cells.forEach(cell => {
        const cr = cell.getBoundingClientRect();
        const overlaps =
          cr.left < selViewport.right &&
          cr.right > selViewport.left &&
          cr.top < selViewport.bottom &&
          cr.bottom > selViewport.top;

        if (overlaps) {
          const matIdx = parseInt(cell.dataset.matIdx);
          const colIdx = parseInt(cell.dataset.colIdx);
          const rowType = cell.dataset.rowType;
          const entryIdx = rowType === 'entry' ? parseInt(cell.dataset.entryIdx) : null;

          if (!this.getCellAmount(rowType, matIdx, entryIdx, colIdx)) return;

          if (rowType === 'entry') {
            newSelected[`entry-${matIdx}-${entryIdx}-col-${colIdx}`] = { matIdx, entryIdx, colIdx, rowType };
          } else {
            newSelected[`mat-${matIdx}-col-${colIdx}`] = { matIdx, colIdx, rowType };
          }
        }
      });

      this.selectedCells = newSelected;
    },

    showSelectionSummary() {
      const byMaterial = {};

      for (const [key, info] of Object.entries(this.selectedCells)) {
        const mat = this.materials[info.matIdx];
        if (!mat) continue;
        const col = this.displayColumns[info.colIdx];
        if (!col) continue;

        const matId = mat.material_id;
        if (!byMaterial[matId]) {
          byMaterial[matId] = {
            material_name: mat.material_name,
            unit_name: mat.unit_name || '',
            total_amount: 0,
            total_delivered: 0,
            required_item_ids: [],
          };
        }
        const summary = byMaterial[matId];

        if (info.rowType === 'entry') {
          const entry = mat.entries[info.entryIdx];
          if (!entry) continue;
          for (const d of col.dates) {
            const di = entry._dateItems[d];
            if (di) {
              summary.total_amount += di.amount || 0;
              summary.total_delivered += di.delivered || 0;
              if (di.required_item_id) {
                summary.required_item_ids.push(di.required_item_id);
              }
            }
          }
        } else {
          for (const entry of mat.entries) {
            for (const d of col.dates) {
              const di = entry._dateItems[d];
              if (di) {
                summary.total_amount += di.amount || 0;
                summary.total_delivered += di.delivered || 0;
                if (di.required_item_id) {
                  summary.required_item_ids.push(di.required_item_id);
                }
              }
            }
          }
        }
      }

      this.selectionSummary = Object.values(byMaterial).map(s => ({
        ...s,
        total_amount: parseFloat(s.total_amount.toFixed(4)),
        total_delivered: parseFloat(s.total_delivered.toFixed(4)),
        remaining: parseFloat((s.total_amount - s.total_delivered).toFixed(4)),
      }));
      this.showSelectionDialog = true;
    },
  },
};
</script>

<style scoped lang="scss">
.required-items-page {
  padding: 8px 12px;
}

.required-items-page__header {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}

.required-items-page__title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.required-items-page__project {
  font-size: 12px;
  color: #5a7d6a;
  background: #eef4f0;
  padding: 2px 10px;
  border-radius: 12px;
  margin-left: 10px;
  white-space: nowrap;
}

.ri-progress-section {
  margin-bottom: 6px;
}

.ri-progress-bar {
  position: relative;
  height: 22px;
  background: #e8e8e8;
  border-radius: 11px;
  overflow: hidden;
}

.ri-progress-segment {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  border-radius: 0 8px 8px 0;
  transition: width 0.4s ease;
}

.ri-progress--required { background: #ff9800; z-index: 1; }
.ri-progress--ordered  { background: #2196f3; z-index: 2; }
.ri-progress--delivered { background: #4caf50; z-index: 3; }

.ri-progress-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-top: 4px;
}

.ri-legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.ri-legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.ri-legend-text {
  font-size: 11px;
  color: #555;
  white-space: nowrap;
}

.ri-progress-tooltip {
  font-size: 12px;
  line-height: 1.6;
}

.ri-tooltip-row {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.ri-tooltip-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}


.required-items-table-wrapper {
  overflow: auto;
  max-height: calc(100vh - 90px);
  border: 1px solid #d5e1d9;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04),
    0 4px 12px rgba(16, 24, 40, 0.05);
}

.ri-table {
  width: max-content;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
}

.ri-th {
  padding: 3px 3px;
  text-align: center;
  font-weight: 500;
  white-space: nowrap;
  border-bottom: 1px solid #d5e1d9;
  border-right: 1px solid #d5e1d9;
  background: #eef4f0;
  position: sticky;
  top: 0;
  z-index: 5;
}

.ri-th--fixed {
  background: #e5ede8;
}

.ri-sticky {
  position: sticky;
  z-index: 2;
}

.ri-th.ri-sticky {
  z-index: 6;
}

.ri-sticky--0 { left: 0; min-width: 32px; width: 32px; }
.ri-sticky--1 { left: 32px; min-width: 180px; width: 180px; }
.ri-sticky--2 { left: 212px; min-width: 40px; width: 40px; }
.ri-sticky--3 { left: 252px; min-width: 40px; width: 40px; }
.ri-sticky--4 { left: 292px; min-width: 40px; width: 40px; }
.ri-sticky--5 {
  left: 332px;
  min-width: 40px;
  width: 40px;
  border-right: 2px solid #c3d4c8;
}

.ri-row--material .ri-sticky { background: #f3f8f4; }
.ri-row--material:hover .ri-sticky { background: #e5ede8; }
.ri-row--entry .ri-sticky { background: #fafcfa; }
.ri-row--entry:hover .ri-sticky { background: #f0f5f1; }

.ri-th--name {
  min-width: 140px;
  max-width: 180px;
  text-align: center;
}

.ri-th--month {
  font-weight: 600;
  font-size: 12px;
  text-transform: capitalize;
  text-align: left;
}

.ri-month-label {
  position: sticky;
  left: 374px;
}

.ri-th--day {
  font-size: 10px;
  padding: 2px 2px;
}

.ri-th--sub {
  font-size: 9px;
  font-weight: 400;
  padding: 1px 2px;
}

.ri-th--month.ri-month--0 { background: #d0e4d6; }
.ri-th--month.ri-month--1 { background: #e2e9d8; }

.ri-th--day.ri-month--0 { background: #ddeae0; }
.ri-th--day.ri-month--1 { background: #eaede0; }

.ri-th--sub.ri-month--0 { background: #e8f0ea; }
.ri-th--sub.ri-month--1 { background: #f0f3e8; }

.ri-td.ri-month--0 { background: #f5f9f6; }
.ri-td.ri-month--1 { background: #f9faf4; }

.ri-row--material .ri-td.ri-month--0 { background: #eaf1eb; }
.ri-row--material .ri-td.ri-month--1 { background: #f1f4ea; }
.ri-row--material:hover .ri-td.ri-month--0 { background: #ddeae0; }
.ri-row--material:hover .ri-td.ri-month--1 { background: #e8ecdf; }

.ri-row--entry .ri-td.ri-month--0 { background: #f5f9f6; }
.ri-row--entry .ri-td.ri-month--1 { background: #f9faf4; }
.ri-row--entry:hover .ri-td.ri-month--0 { background: #eaf1eb; }
.ri-row--entry:hover .ri-td.ri-month--1 { background: #f1f4ea; }

.ri-td {
  padding: 2px 3px;
  border-bottom: 1px solid #d5e1d9;
  border-right: 1px solid #d5e1d9;
  vertical-align: middle;
  white-space: nowrap;
}

.ri-td--name {
  white-space: normal;
  word-break: break-word;
  max-width: 180px;
  cursor: pointer;
}

.ri-name-cell {
  display: flex;
  align-items: center;
  gap: 2px;
}

.ri-name-text {
  flex: 1;
  min-width: 0;
}

.ri-td--entry-name {
  padding-left: 28px !important;
  color: #555;
  white-space: normal;
  word-break: break-word;
  max-width: 180px;
}

.ri-td--num {
  text-align: right;
  white-space: nowrap;
}

.ri-td--date {
  font-size: 11px;
  color: #666;
}

.ri-td--delivered {
  font-size: 11px;
  color: #888;
  background: #fafafa;
}

.ri-td--complete {
  background: #e8f5e9 !important;
  color: #2e7d32 !important;
  font-weight: 600;
}

.ri-row--material-sticky > td {
  position: sticky;
  top: var(--thead-h, 0px);
  z-index: 1;
  background: #f3f8f4;
}

.ri-row--material-sticky:hover > td {
  background: #e5ede8;
}

.ri-row--material-sticky > td.ri-sticky {
  z-index: 3;
}

.ri-row--material {
  font-weight: 600;
  background: #f3f8f4;
  cursor: pointer;

  &:hover {
    background: #e5ede8;
  }
}

.ri-row--entry {
  background: #fafcfa;

  &:hover {
    background: #f0f5f1;
  }
}

.ri-row--levels {
  background: #eef4f0;
  font-weight: 500;
  font-size: 11px;
}

.ri-td--level-sticky {
  position: sticky;
  top: var(--header-h, 0px);
  z-index: 2;
  background: #eef4f0;
}

.ri-td--level-sticky.ri-sticky {
  z-index: 4;
  background: #eef4f0;
}

.ri-row--levels .ri-sticky {
  background: #eef4f0;
}

.ri-td--level-label {
  font-weight: 600;
  font-size: 12px;
  color: #5a7d6a;
  white-space: nowrap;
}

.ri-td--level-active {
  text-align: left;
  font-weight: 500;
  font-size: 11px;
}

.ri-level-label {
  position: sticky;
  left: 374px;
  white-space: nowrap;
}

.expand-icon {
  color: #5a7d6a;
  vertical-align: middle;
  margin-right: 4px;
}

.ri-view-toggle {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}

.ri-td--readonly {
  background: #f6f9f6;
  font-size: 11px;
  color: #666;
}

.required-items-table-wrapper {
  position: relative;
}

.ri-selecting-mode.ri-tool-rect {
  cursor: crosshair;
}

.ri-selecting-mode.ri-tool-click {
  cursor: default;
}

.ri-dragging {
  user-select: none;
  -webkit-user-select: none;
}

.ri-selection-rect {
  position: absolute;
  background: rgba(90, 125, 106, 0.10);
  border: 1.5px dashed #5a7d6a;
  pointer-events: none;
  z-index: 10;
  border-radius: 2px;
}

.ri-td--selected {
  background: #ddeae0 !important;
  box-shadow: inset 0 0 0 1.5px #5a7d6a;
}

.ri-th--click-target {
  cursor: pointer;

  &:hover {
    background: #e5ede8 !important;
  }
}

.ri-td--click-target {
  cursor: pointer;

  &:hover {
    background: #eef4f0 !important;
  }
}

.ri-tool-toggle {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}

.ri-summary-table {
  width: 100%;

  th {
    font-size: 13px;
    background: #eef4f0;
  }

  td {
    font-size: 13px;
  }
}
</style>
